import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Star } from "lucide-react";

export function Stars({ rating, size = 15 }) {
  return (
    <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= rating ? "fill-amber-400 text-amber-400" : "text-border"}
        />
      ))}
    </div>
  );
}

export default function ReviewsSection() {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data || [])).catch(() => {});
  }, []);

  if (reviews.length === 0) return null;

  const avg = (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1);

  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 md:py-32 bg-card">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="overline mb-4">Guest Stories</p>
            <h2 className="font-display text-4xl sm:text-5xl text-foreground font-medium leading-[1.05] tracking-tight max-w-xl">
              What our guests say about their stay.
            </h2>
          </div>
          <div className="flex items-center gap-3" data-testid="reviews-average">
            <span className="font-display text-5xl text-primary">{avg}</span>
            <div>
              <Stars rating={Math.round(Number(avg))} size={18} />
              <p className="font-sans text-xs text-muted-foreground mt-1">
                from {reviews.length} guest review{reviews.length > 1 ? "s" : ""}
              </p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reviews-grid">
          {reviews.map((r) => (
            <article
              key={r.id}
              data-testid={`review-card-${r.id}`}
              className="rounded-2xl bg-background border border-border p-7 flex flex-col gap-4 hover:shadow-md transition-shadow"
            >
              <Stars rating={r.rating} />
              <p className="font-sans text-[15px] leading-relaxed text-foreground/85 flex-1">
                “{r.text}”
              </p>
              <div className="pt-3 border-t border-border">
                <p className="font-sans text-sm font-semibold text-foreground">{r.guest_name}</p>
                <p className="font-sans text-xs text-muted-foreground mt-0.5">
                  {[r.room_name, r.stay_month].filter(Boolean).join(" · ")}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
